import { DecimalPipe } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { SimuladorGoApi } from '@gestor-web/@core/api/go';
import {
  DetalheAtributosQualidade,
  DetalhePrestadorCnes,
  DetalhePrestadorMedicos,
  DetalhePrestadorRps,
  DetalheReceitaFederal,
  LeitoCnes,
  Operadora,
  ServicoEspecializadoCnes,
  VinculoUsuarioOperadora,
} from '@gestor-web/@core/api/ords/model';
import { OperadorasSelectors, UserProfileSelectors } from '@gestor-web/@core/store/selectors';
import { Store } from '@ngrx/store';
import { buildGridOptions } from '@gestor-web/app/shared/components';
import { CepPipe } from '@gestor-web/app/shared/pipes/cep.pipe';
import { CompetenciaPipe } from '@gestor-web/app/shared/pipes/competencia.pipe';
import { CpfCnpjPipe } from '@gestor-web/app/shared/pipes/cpf-ou-cnpj.pipe';
import { FuncionalidadeAcesso, GeralService, NotificationService, PermissaoService } from '@gestor-web/app/shared/services';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, ColGroupDef, GridOptions } from 'ag-grid-community';
import { combineLatest, finalize, forkJoin, Observable, Subject, Subscription, takeUntil } from 'rxjs';

const COLUMNS_LEITOS_CNES: ColDef<LeitoCnes>[] = [
  {
    headerName: 'Atributos',
    field: 'ds_atributo',
  },
  {
    headerName: 'Leitos',
    colId: 'ds_leito',
    flex: 1,
    minWidth: 200,
    valueGetter: params => (params.data ? `${params.data.cd_leito} - ${params.data.ds_leito}` : null),
  },
  {
    headerName: 'Qtde. leitos',
    field: 'qt_existente',
    type: 'int',
  },
  {
    headerName: 'Qtde. leitos SUS',
    field: 'qt_sus',
    type: 'int',
  },
];

const colunaSimNaoServico = (headerName: string, field: keyof ServicoEspecializadoCnes): ColDef<ServicoEspecializadoCnes> => ({
  headerName,
  colId: field,
  width: 110,
  valueGetter: params => (params.data?.[field] === 1 ? 'Sim' : 'Não'),
});

const COLUMNS_SERVICOS_ESPECIALIZADOS_CNES: (ColDef<ServicoEspecializadoCnes> | ColGroupDef<ServicoEspecializadoCnes>)[] = [
  {
    headerName: 'Serviços especializados',
    colId: 'ds_servico_especializado',
    flex: 1,
    minWidth: 250,
    valueGetter: params =>
      params.data ? `${params.data.cd_servico_especializado} - ${params.data.ds_servico_especializado}` : null,
  },
  {
    headerName: 'Atributos',
    field: 'ds_atributo',
  },
  {
    headerName: 'Ambulatorial',
    children: [
      colunaSimNaoServico('SUS', 'ib_ambulatorial_sus'),
      colunaSimNaoServico('Não SUS', 'ib_ambulatorial_n_sus'),
    ],
  },
  {
    headerName: 'Hospitalar',
    children: [
      colunaSimNaoServico('SUS', 'ib_hospitalar_sus'),
      colunaSimNaoServico('Não SUS', 'ib_hospitalar_n_sus'),
    ],
  },
];

const COLUMNS_CNES: ColDef[] = [
  {
    headerName: 'Nr. CNES',
    field: 'nr_cnes',
  },
  {
    headerName: 'Nome fantasia',
    field: 'nm_fantasia',
  },
  {
    headerName: 'Nome empresarial',
    field: 'nm_empresarial',
  },
  {
    headerName: 'Tipo pessoa',
    field: 'tp_pessoa',
  },
  {
    headerName: 'CPF/CNPJ',
    field: 'nr_cpf_cnpj',
    type: 'cpfCnpj',
  },
  {
    headerName: 'Situação ativo (ID)',
    field: 'ib_situacao',
    hide: true,
  },
  {
    headerName: 'Situação ativo',
    field: 'nm_ib_situacao',
  },
  {
    headerName: 'Cód. tipo estabelecimento',
    field: 'cd_tipo_estabelecimento',
  },
  {
    headerName: 'Descrição tipo estabelecimento',
    field: 'ds_tipo_estabelecimento',
  },
  {
    headerName: 'Cód. natureza jurídica',
    field: 'cd_natureza_juridica',
  },
  {
    headerName: 'Descrição natureza jurídica',
    field: 'ds_natureza_juridica',
  },
  {
    headerName: 'CEP',
    field: 'nr_cep',
    valueFormatter: params => new CepPipe().transform(params.value),
  },
  /*
  {
    headerName: 'Logradouro',
    field: 'ds_logradouro',
  },
  {
    headerName: 'Nr. Logradouro',
    field: 'nr_logradouro',
  },
  {
    headerName: 'Complemento',
    field: 'ds_complemento',
  },
  {
    headerName: 'Bairro',
    field: 'ds_bairro',
  },
  */
  {
    headerName: 'Cód. município',
    field: 'cd_municipio',
  },
  {
    headerName: 'Nome município',
    field: 'nm_municipio',
  },
  {
    headerName: 'UF',
    field: 'sg_uf',
  },
  {
    headerName: 'Urgência/Emergência (ID)',
    field: 'ib_urgencia_emergencia_24h',
    hide: true,
  },
  {
    headerName: 'Urgência/Emergência',
    field: 'nm_ib_urgencia_emergencia_24h',
  },
  {
    headerName: 'Região de saúde',
    field: 'nm_regiao_saude',
  },
];

const COLUMNS_RPS: ColDef[] = [
  {
    headerName: 'Nr. competência',
    field: 'nr_competencia',
    type: 'competencia',
    width: 100,
  },
  {
    headerName: 'Rede hospitalar (ID)',
    field: 'ib_rede_hospitalar',
    hide: true,
  },
  {
    headerName: 'Rede hospitalar',
    field: 'nm_ib_rede_hospitalar',
  },
  {
    headerName: 'Nr. CPF/CNPJ',
    field: 'nr_cpf_cnpj',
    type: 'cpfCnpj',
  },
  {
    headerName: 'Nr. CNES',
    field: 'nr_cnes',
  },
  {
    headerName: 'Nr. prestador',
    field: 'nr_prestador_ops',
  },
  {
    headerName: 'Nome do prestador',
    field: 'nm_prestador',
  },
  {
    headerName: 'Cód. município',
    field: 'cd_municipio_semdv',
  },
  {
    headerName: 'Nome município',
    field: 'nm_municipio',
  },
  {
    headerName: 'UF',
    field: 'sg_uf',
  },
  {
    headerName: 'Classificação (ID)',
    field: 'id_classificacao',
    hide: true,
  },
  {
    headerName: 'Classificação',
    field: 'nm_id_classificacao',
  },
  {
    headerName: 'Urgência/Emergência (ID)',
    field: 'sn_urgencia_emergencia',
    hide: true,
  },
  {
    headerName: 'Urgência/Emergência',
    field: 'nm_sn_urgencia_emergencia',
  },
  {
    headerName: 'Tipo relação operadora (ID)',
    field: 'tp_relacao_operadora',
    hide: true,
  },
  {
    headerName: 'Tipo relação operadora',
    field: 'nm_tp_relacao_operadora',
  },
  {
    headerName: 'Tipo contratualização (ID)',
    field: 'tp_contratualizacao',
    hide: true,
  },
  {
    headerName: 'Tipo contratualização',
    field: 'nm_tp_contratualizacao',
  },
  {
    headerName: 'Tipo disponibilidade (ID)',
    field: 'tp_disponibilidade',
    hide: true,
  },
  {
    headerName: 'Tipo disponibilidade',
    field: 'nm_tp_disponibilidade',
  },
  {
    headerName: 'Data início vínculo',
    field: 'dt_vinculo_inicio',
    type: 'date',
  },
  {
    headerName: 'Data fim vínculo',
    field: 'dt_vinculo_fim',
    type: 'date',
  },
  {
    headerName: 'Cód. vínculo rede',
    field: 'cd_vinculo_rede',
  },
  {
    headerName: 'Cód. plano ANS',
    field: 'cd_plano_ans',
  },
  {
    headerName: 'Nr. registro plano',
    field: 'nr_registro_plano',
  },
  {
    headerName: 'Nome do plano',
    field: 'nm_plano',
  },
];

const COLUMNS_MEDICOS: ColDef[] = [
  {
    headerName: 'Nr. CPF',
    field: 'nr_cpf',
    type: 'cpf',
  },
  {
    headerName: 'Nome do médico',
    field: 'nm_medico',
  },
  {
    headerName: 'Nr. CRM',
    field: 'nr_crm',
  },
  {
    headerName: 'UF',
    field: 'sg_uf',
    maxWidth: 100,
  },
  {
    headerName: 'Cód. situação (ID)',
    field: 'cd_situacao',
    hide: true,
  },
  {
    headerName: 'Cód. situação',
    field: 'nm_cd_situacao',
  },
  {
    headerName: 'Cód. CBO',
    field: 'cd_cbo',
    maxWidth: 150,
  },
  {
    headerName: 'Descrição CBO',
    field: 'ds_titulo_cbo',
  },
];

const COLUMNS_ATRIBUTOS_QUALIDADE: ColDef[] = [
  {
    headerName: 'Nr. CPF/CNPJ',
    field: 'nr_cnpj',
    type: 'cpfCnpj',
  },
  {
    headerName: 'Nr. CNES',
    field: 'nr_cnes',
  },
  {
    headerName: 'Atributo de qualidade',
    field: 'sg_acreditacao',
  },
];

const COLUMNS_RECEITA_FEDERAL: ColDef[] = [
  {
    headerName: 'Nome empresarial',
    field: 'nm_empresarial',
    width: 200,
  },
  {
    headerName: 'Título do estabelecimento',
    field: 'nm_fantasia',
    width: 220,
  },
  {
    headerName: 'Código e descrição da atividade econômica principal',
    field: 'ds_cnae_principal',
    width: 350,
  },
  {
    headerName: 'Código e descrição das atividades econômicas secundárias',
    field: 'ds_cnaes_secundarios',
    width: 380,
    cellStyle: {
      whiteSpace: 'pre-line',
      lineHeight: '2',
    },
    wrapText: true,
    autoHeight: true,
  },
];

@Component({
    selector: 'bl-detalhe-prestador',
    templateUrl: './detalhe-prestador.component.html',
    styleUrls: ['./detalhe-prestador.component.scss'],
    standalone: false
})
export class DetalhePrestadorComponent implements OnInit, OnChanges {
  dialogPrestadorExportExcel() {
    throw new Error('Method not implemented.');
  }

  @ViewChild('agGridCnes') agGridCnes: AgGridAngular<RowPrestadorCnes>;
  @ViewChild('agGridRps') agGridRps: AgGridAngular<RowPrestadorRps>;
  @ViewChild('agGridMedicos') agGridMedicos: AgGridAngular<RowPrestadorMedicos>;
  @ViewChild('agGridAtributosQualidade') agGridAtributosQualidade: AgGridAngular<RowAtributosQualidade>;
  @ViewChild('agGridReceitaFederal') agGridReceitaFederal: AgGridAngular<RowDetalheReceitaFederal>;

  gridOptionsCnes: GridOptions<RowPrestadorCnes>;
  gridOptionsRps: GridOptions<RowPrestadorRps>;
  gridOptionsMedicos: GridOptions<RowPrestadorMedicos>;
  gridOptionsAtributosQualidade: GridOptions<RowAtributosQualidade>;
  gridOptionsReceitaFederal: GridOptions<any>;
  gridOptionsLeitosCnes: GridOptions<LeitoCnes>;
  gridOptionsServicosEspecializadosCnes: GridOptions<ServicoEspecializadoCnes>;

  @Input()
  cdGuiaMonitor: string;

  @Input()
  nrCnpjCpfExec: string;

  @Input()
  nrCnesExec: string;

  @Input()
  nrRegistroPlano: string;

  @Output()
  onChange = new EventEmitter<string>();

  operadoraWorkspace: Operadora | VinculoUsuarioOperadora;

  rowDadosReceitaFederal: RowDetalheReceitaFederal[] = [];
  rowDadosCnes: RowPrestadorCnes[] = [];
  rowDadosRps: RowPrestadorRps[] = [];
  rowDadosMedicos: RowPrestadorMedicos[] = [];
  rowDadosAtributosQualidade: RowAtributosQualidade[] = [];
  rowLeitosCnes: LeitoCnes[] = [];
  rowServicosEspecializadosCnes: ServicoEspecializadoCnes[] = [];

  nrCnesEncontrado: string = null;
  carregandoLeitosServicosCnes = false;

  private leitosServicosCnesSub: Subscription;

  acessoAbaDadosRPS$: Observable<FuncionalidadeAcesso>;

  constructor(
    private simuladorGoApi: SimuladorGoApi,
    private geralService: GeralService,
    private permissaoService: PermissaoService,
    private notificationService: NotificationService,
    private decimalPipe: DecimalPipe,
    private cpfCnpjPipe: CpfCnpjPipe,
    private competenciaPipe: CompetenciaPipe,
    private store: Store,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    combineLatest([
      this.store.select(OperadorasSelectors.selectWorkspaceOperadora),
      this.store.select(UserProfileSelectors.selectUserIdssProfile),
    ])
      .pipe(takeUntil(this.ngDestroyed$))
      .subscribe(([operadora]) => {
        this.operadoraWorkspace = operadora;

        this.configureGrid();

        // Carrega os dados se os inputs já estiverem disponíveis
        if (this.nrCnpjCpfExec) {
          this.loadPrestadorData();
        }
      });

    this.acessoAbaDadosRPS$ = this.permissaoService.checkExibicaoFeature('DADOS_PRESTADOR.DADOS_RPS');
  }

  private configureGrid() {
    this.gridOptionsCnes = buildGridOptions<RowPrestadorCnes>(
      {
        columnDefs: COLUMNS_CNES,
        pipes: {
          decimal: this.decimalPipe,
          cpfCnpj: this.cpfCnpjPipe,
        },
      },
      {
        sideBar: null,
        statusBar: null,
        grandTotalRow: null,
        domLayout: 'autoHeight',
      },
    );
    this.gridOptionsRps = buildGridOptions<RowPrestadorRps>(
      {
        columnDefs: COLUMNS_RPS,
        pipes: {
          decimal: this.decimalPipe,
          competencia: this.competenciaPipe,
          cpfCnpj: this.cpfCnpjPipe,
        },
      },
      {
        sideBar: null,
        statusBar: null,
        grandTotalRow: null,
      },
    );
    this.gridOptionsMedicos = buildGridOptions<RowPrestadorMedicos>(
      {
        columnDefs: COLUMNS_MEDICOS,
        pipes: {
          cpfCnpj: this.cpfCnpjPipe,
        },
      },
      {
        sideBar: null,
        statusBar: null,
        grandTotalRow: null,
      },
    );
    this.gridOptionsAtributosQualidade = buildGridOptions<RowAtributosQualidade>(
      {
        columnDefs: COLUMNS_ATRIBUTOS_QUALIDADE,
        pipes: {
          cpfCnpj: this.cpfCnpjPipe,
        },
      },
      {
        sideBar: null,
        statusBar: null,
        grandTotalRow: null,
      },
    );
    this.gridOptionsLeitosCnes = buildGridOptions<LeitoCnes>(
      {
        columnDefs: COLUMNS_LEITOS_CNES,
        pipes: {
          decimal: this.decimalPipe,
        },
      },
      {
        sideBar: null,
        statusBar: null,
        grandTotalRow: null,
        domLayout: 'autoHeight',
      },
    );
    this.gridOptionsServicosEspecializadosCnes = buildGridOptions<ServicoEspecializadoCnes>(
      {
        columnDefs: COLUMNS_SERVICOS_ESPECIALIZADOS_CNES,
      },
      {
        sideBar: null,
        statusBar: null,
        grandTotalRow: null,
        domLayout: 'autoHeight',
      },
    );
    this.gridOptionsReceitaFederal = buildGridOptions<any>(
      {
        columnDefs: COLUMNS_RECEITA_FEDERAL,
      },
      {
        sideBar: null,
        statusBar: null,
        grandTotalRow: null,
      },
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Só carrega se a operadora já estiver disponível
    if (this.operadoraWorkspace?.cd_registro_ans && this.nrCnpjCpfExec) {
      this.loadPrestadorData();
    }
  }

  private loadPrestadorData(): void {
    if (!this.nrCnpjCpfExec || !this.operadoraWorkspace?.cd_registro_ans) {
      return;
    }

    this.setReceitaFederalLoading(true);

    forkJoin([
      this.simuladorGoApi.fetchPrestadorExecutanteReceitaFederal(this.operadoraWorkspace.cd_registro_ans, this.nrCnpjCpfExec),
      this.simuladorGoApi.fetchPrestadorExecutanteDadosCnes(
        this.operadoraWorkspace.cd_registro_ans,
        this.nrCnpjCpfExec,
        this.nrCnesExec,
      ),
      this.simuladorGoApi.fetchPrestadorExecutanteDadosRps(
        this.operadoraWorkspace.cd_registro_ans,
        this.nrCnpjCpfExec,
        this.nrRegistroPlano,
      ),
      this.simuladorGoApi.fetchPrestadorExecutanteDadosMedico(this.operadoraWorkspace.cd_registro_ans, this.nrCnpjCpfExec),
      this.simuladorGoApi.fetchPrestadorExecutanteDadosAtributosQualidade(
        this.operadoraWorkspace.cd_registro_ans,
        this.nrCnpjCpfExec,
      ),
    ])
      .pipe(
        takeUntil(this.ngDestroyed$),
        finalize(() => this.setReceitaFederalLoading(false)),
      )
      .subscribe({
        next: ([rowDadosReceitaFederal, rowDadosCnes, rowDadosRps, rowDadosMedico, rowDadosAtributosQualidade]) => {
          // Receita Federal
          const receitaItems =
            (rowDadosReceitaFederal as any)?.items ??
            (Array.isArray(rowDadosReceitaFederal)
              ? (rowDadosReceitaFederal as any[])
              : rowDadosReceitaFederal
                ? [rowDadosReceitaFederal as any]
                : []);
          this.rowDadosReceitaFederal = (receitaItems?.length ? receitaItems : []) as RowDetalheReceitaFederal[];

          // CNES
          const cnesItems: DetalhePrestadorCnes[] =
            (rowDadosCnes as any)?.items ??
            (Array.isArray(rowDadosCnes) ? (rowDadosCnes as any[]) : rowDadosCnes ? [rowDadosCnes as any] : []);

          this.rowDadosCnes = (cnesItems as DetalhePrestadorCnes[]).map(cnes => ({
            ...cnes,
            nm_ib_situacao: this.geralService.buildNomeDeDominio((cnes as any).ib_situacao, 'LOGICO_SIM_NAO'),
            nm_ib_urgencia_emergencia_24h: this.geralService.buildNomeDeDominio(
              (cnes as any).ib_urgencia_emergencia_24h,
              'LOGICO_SIM_NAO',
            ),
          }));
          this.loadLeitosServicosCnes(cnesItems[0]?.nr_cnes);

          // RPS (com mapeamento completo)
          const rpsItems: DetalhePrestadorRps[] =
            (rowDadosRps as any)?.items ??
            (Array.isArray(rowDadosRps) ? (rowDadosRps as any[]) : rowDadosRps ? [rowDadosRps as any] : []);

          this.rowDadosRps = (rpsItems as DetalhePrestadorRps[]).map(rps => {
            const row_rps: RowPrestadorRps = {
              ...rps,
              nm_ib_rede_hospitalar: this.geralService.buildNomeDeDominio(
                (rps as any).ib_rede_hospitalar,
                'LOGICO_SIM_NAO',
              ),
              nm_id_classificacao: this.geralService.buildNomeDeDominio(
                (rps as any).id_classificacao,
                'RPS_CLASSIFICACAO_PRESTADOR',
              ),
              nm_sn_urgencia_emergencia: this.geralService.buildNomeDeDominio(
                (rps as any).sn_urgencia_emergencia,
                'RPS_URGENCIA_EMERGENCIA',
              ),
              nm_tp_relacao_operadora: this.geralService.buildNomeDeDominio(
                (rps as any).tp_relacao_operadora,
                'RPS_RELACAO_OPERADORA',
              ),
              nm_tp_contratualizacao: this.geralService.buildNomeDeDominio(
                (rps as any).tp_contratualizacao,
                'RPS_TIPO_CONTRATUALIZACAO',
              ),
              nm_tp_disponibilidade: this.geralService.buildNomeDeDominio(
                (rps as any).tp_disponibilidade,
                'RPS_DISPONIBILIDADE_SERVICO ',
              ),
            };
            return row_rps;
          });

          // Médico
          const medicoItems: DetalhePrestadorMedicos[] =
            (rowDadosMedico as any)?.items ??
            (Array.isArray(rowDadosMedico) ? (rowDadosMedico as any[]) : rowDadosMedico ? [rowDadosMedico as any] : []);

          this.rowDadosMedicos = (medicoItems as DetalhePrestadorMedicos[]).map(medico => ({
            ...medico,
            nm_cd_situacao: this.geralService.buildNomeDeDominio((medico as any).cd_situacao, 'CFM_SITUACAO'),
          }));

          // Atributos de Qualidade (normaliza para array)
          const atributosItems =
            (rowDadosAtributosQualidade as any)?.items ??
            (Array.isArray(rowDadosAtributosQualidade)
              ? (rowDadosAtributosQualidade as any[])
              : rowDadosAtributosQualidade
                ? [rowDadosAtributosQualidade as any]
                : []);

          this.rowDadosAtributosQualidade = (atributosItems?.length ? atributosItems : []) as RowAtributosQualidade[];

          // Força a detecção de mudanças para atualizar a view
          this.cdr.detectChanges();
        },
        error: error => this.notificationService.handleError(error),
        complete: () => {
          this.agGridReceitaFederal?.api?.setGridOption('loading', false);
          this.agGridCnes?.api?.setGridOption('loading', false);
          this.agGridRps?.api?.setGridOption('loading', false);
          this.agGridMedicos?.api?.setGridOption('loading', false);
          this.agGridAtributosQualidade?.api?.setGridOption('loading', false);

          this.cdr.detectChanges();
        },
      });
  }

  private loadLeitosServicosCnes(nrCnes: string): void {
    this.leitosServicosCnesSub?.unsubscribe();
    this.nrCnesEncontrado = nrCnes || null;
    this.rowLeitosCnes = [];
    this.rowServicosEspecializadosCnes = [];

    if (!this.nrCnesEncontrado) {
      return;
    }

    const regans = this.operadoraWorkspace.cd_registro_ans;
    this.carregandoLeitosServicosCnes = true;
    this.leitosServicosCnesSub = forkJoin([
      this.simuladorGoApi.fetchPrestadorExecutanteLeitosCnes(regans, this.nrCnesEncontrado),
      this.simuladorGoApi.fetchPrestadorExecutanteServicosEspecializadosCnes(regans, this.nrCnesEncontrado),
    ])
      .pipe(
        takeUntil(this.ngDestroyed$),
        finalize(() => {
          this.carregandoLeitosServicosCnes = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: ([leitos, servicos]) => {
          this.rowLeitosCnes = leitos?.items ?? [];
          this.rowServicosEspecializadosCnes = servicos?.items ?? [];
        },
        error: error => this.notificationService.handleError(error),
      });
  }

  private setReceitaFederalLoading(isLoading: boolean) {
    this.agGridReceitaFederal?.api?.setGridOption('loading', isLoading);
  }

  private ngDestroyed$ = new Subject<void>();
  ngOnDestroy() {
    this.ngDestroyed$.next();
    this.ngDestroyed$.complete();
  }
}

export interface RowPrestadorCnes extends DetalhePrestadorCnes {
  nm_ib_urgencia_emergencia_24h: string;
  nm_ib_situacao: string;
}

export interface RowPrestadorRps extends DetalhePrestadorRps {
  nm_ib_rede_hospitalar: string;
  nm_id_classificacao: string;
  nm_sn_urgencia_emergencia: string;
  nm_tp_relacao_operadora: string;
  nm_tp_contratualizacao: string;
  nm_tp_disponibilidade: string;
}

export interface RowPrestadorMedicos extends DetalhePrestadorMedicos {
  nm_cd_situacao: string;
}

export interface RowAtributosQualidade extends DetalheAtributosQualidade {}

export interface RowDetalheReceitaFederal extends DetalheReceitaFederal {}
